// src/features/hotels-overview/components/HotelsOverviewPage.tsx
//
// «Instalaciones» (mockup 09, formerly «Hoteles») — route /hotels-overview
// (distinct from the admin `hotels` resource). Rows with an `arrivalsPerDay`
// sparkline that opens an enlarged chart on click, null cells rendered as «—»
// (never 0), a chain-average row, «solo con llegadas» toggle.
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { formatCount, formatMoney, formatPct, formatSeconds } from '@/lib/analyticsFormat';
import { MiniSparkline } from './MiniSparkline';
import { StackedBarsChart } from '@/components/common/analytics/StackedBarsChart';
import type { HotelsOverviewResponse, HotelsOverviewRow } from '@/types/analytics';

export const HotelsOverviewPage: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const exportCsv = useCsvExport({ page: 'instalaciones' });
  const theme = useTheme();
  const canExport = hasResourcePermission('hotels-overview', 'export');

  const [period, setPeriod] = useAnalyticsPeriod();
  const [onlyWithArrivals, setOnlyWithArrivals] = useState(false);
  const [enlarged, setEnlarged] = useState<HotelsOverviewRow | null>(null);

  const { data, loading, error, refresh } = useAnalyticsFetch<HotelsOverviewResponse>(
    () => {
      const sp: any = scope.scopeParams;
      return (dataProvider as any).getAnalyticsHotels({ hotelIds: sp.hotelIds, groupId: sp.groupId, from: period.from, to: period.to, onlyWithArrivals });
    },
    [dataProvider, scope.scopeParams, period.from, period.to, onlyWithArrivals]
  );

  // One point per day of the period, in order, for the enlarged chart.
  const enlargedSeries = useMemo(() => {
    if (!enlarged) return [];
    const start = new Date(`${period.from}T00:00:00Z`);
    return enlarged.arrivalsPerDay.map((count, i) => {
      const day = new Date(start.getTime() + i * 86_400_000);
      return { date: day.toISOString().slice(0, 10), arrivals: count };
    });
  }, [enlarged, period.from]);
  const enlargedTotal = useMemo(() => (enlarged ? enlarged.arrivalsPerDay.reduce((a, b) => a + b, 0) : 0), [enlarged]);
  // «2026-10-13» → «13/10» (day first, as everywhere else in Spanish and British English).
  const formatDay = (value: string | number) => {
    const [, month, day] = String(value).split('-');
    return day && month ? `${day}/${month}` : String(value);
  };

  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data.rows,
      [
        { labelKey: 'resources.hotelsOverview.table.columns.hotel', value: (r: HotelsOverviewRow) => r.name },
        { labelKey: 'resources.hotelsOverview.table.columns.arrivals', value: (r: HotelsOverviewRow) => r.arrivals },
        { labelKey: 'resources.hotelsOverview.table.columns.preCheckInDonePct', value: (r: HotelsOverviewRow) => r.preCheckInDonePct ?? '' },
      ],
      { from: period.from, to: period.to }
    );
  };

  const cell = (value: number | null, formatter: (v: number | null) => string) => formatter(value);

  const hotelColumns: MetricsColumn<HotelsOverviewRow>[] = [
    {
      key: 'hotel',
      label: translate('resources.hotelsOverview.table.columns.hotel'),
      render: (row) => (
        <Box>
          {row.name}
          <Typography component="div" variant="caption" color="text.secondary">
            {translate(`resources.hotelsOverview.source.${row.arrivalsSource}`)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'arrivalsPerDay',
      label: translate('resources.hotelsOverview.table.columns.arrivalsPerDay'),
      render: (row) => (
        <Box
          role="button"
          tabIndex={0}
          aria-label={translate('resources.hotelsOverview.chartDialog.openAria', { name: row.name })}
          onClick={() => setEnlarged(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setEnlarged(row);
            }
          }}
          sx={{ display: 'inline-flex', cursor: 'pointer', borderRadius: 1, p: 0.5, '&:hover, &:focus-visible': { backgroundColor: 'action.hover', outline: 'none' } }}
        >
          <MiniSparkline points={row.arrivalsPerDay} />
        </Box>
      ),
    },
    { key: 'arrivals', label: translate('resources.hotelsOverview.table.columns.arrivals'), align: 'right', render: (row) => formatCount(row.arrivals) },
    { key: 'preCheckInDonePct', label: translate('resources.hotelsOverview.table.columns.preCheckInDonePct'), align: 'right', render: (row) => cell(row.preCheckInDonePct, formatPct) },
    { key: 'resolvedWithoutReceptionPct', label: translate('resources.hotelsOverview.table.columns.resolvedWithoutReceptionPct'), align: 'right', render: (row) => cell(row.resolvedWithoutReceptionPct, formatPct) },
    { key: 'kioskCheckIns', label: translate('resources.hotelsOverview.table.columns.kioskCheckIns'), align: 'right', render: (row) => (row.hasKiosk ? formatCount(row.kioskCheckIns) : '—') },
    { key: 'medianKioskSeconds', label: translate('resources.hotelsOverview.table.columns.medianKioskSeconds'), align: 'right', render: (row) => (row.hasKiosk ? formatSeconds(row.medianKioskSeconds) : '—') },
    { key: 'leftMidwayPct', label: translate('resources.hotelsOverview.table.columns.leftMidwayPct'), align: 'right', render: (row) => (row.hasKiosk ? cell(row.leftMidwayPct, formatPct) : '—') },
    { key: 'charged', label: translate('resources.hotelsOverview.table.columns.charged'), align: 'right', render: (row) => formatMoney(row.charged, row.chargedCurrency ?? 'EUR') },
  ];

  const chainAverageValues: Record<string, React.ReactNode> = data?.chainAverage
    ? {
        arrivals: formatCount(data.chainAverage.arrivals),
        preCheckInDonePct: cell(data.chainAverage.preCheckInDonePct, formatPct),
        resolvedWithoutReceptionPct: cell(data.chainAverage.resolvedWithoutReceptionPct, formatPct),
        kioskCheckIns: cell(data.chainAverage.kioskCheckIns, formatCount),
        medianKioskSeconds: formatSeconds(data.chainAverage.medianKioskSeconds),
        leftMidwayPct: cell(data.chainAverage.leftMidwayPct, formatPct),
        charged: formatMoney(data.chainAverage.charged, data.chainAverage.chargedCurrency ?? 'EUR'),
      }
    : {};

  return (
    <Box>
      <SectionHeader manualScreen="hotels-overview"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
            <FormControlLabel
              control={<Switch size="small" checked={onlyWithArrivals} onChange={(e) => setOnlyWithArrivals(e.target.checked)} />}
              label={translate('resources.hotelsOverview.onlyWithArrivals')}
            />
          </Box>
        }
        subtitle={translate('resources.hotelsOverview.subtitle')}
        helpConfig={{
          titleKey: 'resources.hotelsOverview.help.title',
          descriptionKey: 'resources.hotelsOverview.help.description',
          featureKeys: [
            'resources.hotelsOverview.help.features.source',
            'resources.hotelsOverview.help.features.sparkline',
            'resources.hotelsOverview.help.features.kiosk',
            'resources.hotelsOverview.help.features.onlyWithArrivals',
          ],
          tipKeys: ['resources.hotelsOverview.help.tips.dash'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {error && <FetchErrorBanner onRetry={refresh} />}

      <ResponsiveMetricsTable<HotelsOverviewRow>
        columns={hotelColumns}
        rows={data?.rows ?? []}
        getRowKey={(row) => row.hotelId}
        loading={loading}
        containerSx={{ backgroundColor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        footer={
          data?.chainAverage
            ? { label: translate('resources.hotelsOverview.chainAverage'), getValue: (key) => chainAverageValues[key] }
            : undefined
        }
      />

      <Dialog open={enlarged !== null} onClose={() => setEnlarged(null)} maxWidth="md" fullWidth>
        {enlarged && (
          <>
            <DialogTitle sx={{ pb: 0.5 }}>
              {translate('resources.hotelsOverview.chartDialog.title', { name: enlarged.name })}
              <Typography variant="body2" color="text.secondary" component="div">
                {translate('resources.hotelsOverview.chartDialog.period', { from: period.from, to: period.to })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <StackedBarsChart
                data={enlargedSeries}
                xKey="date"
                series={[{ key: 'arrivals', labelKey: 'resources.hotelsOverview.table.columns.arrivals', color: theme.palette.dataAccent.dark }]}
                height={360}
                formatX={formatDay}
              />
              <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                {translate('resources.hotelsOverview.chartDialog.total', { count: formatCount(enlargedTotal) })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEnlarged(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                {translate('resources.hotelsOverview.chartDialog.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default HotelsOverviewPage;
